import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {EventContainerMovementWorkspaceCapabilityUdoTemplateKey} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/eventContainerMovementWorkspaceCapability/domain/EventContainerMovementWorkspaceCapabilityUdoTemplateKey";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {WorkspaceCapabilityUdoTemplateConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityUdoTemplateConfiguration";
import {z as zod} from "zod";

export type EventContainerMovementWorkspaceCapabilityUdoBindings = WorkspaceCapabilityUdoTemplateConfiguration<WorkspaceCapabilityType.eventContainerMovement> & {
    fixedUdoTemplates: {
        [EventContainerMovementWorkspaceCapabilityUdoTemplateKey.operation]: {
            udoAdditionalColumnIds: {
                actorUserId: Uuid;
                completedAt: Uuid;
                eventContainerTemplateId: Uuid;
                failedCount: Uuid;
                idempotencyKey: Uuid;
                ineligibleCount: Uuid;
                movementStatus: Uuid;
                operationConfigurationId: Uuid;
                operationType: Uuid;
                reason: Uuid;
                remarks: Uuid;
                requestedChanges: Uuid;
                requestMetadata: Uuid;
                startedAt: Uuid;
                succeededCount: Uuid;
                totalCount: Uuid;
            };
        };
        [EventContainerMovementWorkspaceCapabilityUdoTemplateKey.operationItem]: {
            udoAdditionalColumnIds: {
                completedAt: Uuid;
                eventContainerId: Uuid;
                expectedRevisionId: Uuid;
                failureCode: Uuid;
                failureDetails: Uuid;
                movementStatus: Uuid;
                operationId: Uuid;
                requestedTargetValues: Uuid;
                resolvedTargetValues: Uuid;
                selectionIndex: Uuid;
                startedAt: Uuid;
                uniqueKey: Uuid;
            };
        };
        [EventContainerMovementWorkspaceCapabilityUdoTemplateKey.audit]: {
            udoAdditionalColumnIds: {
                actorUserId: Uuid;
                afterValues: Uuid;
                beforeValues: Uuid;
                dependentUpdates: Uuid;
                eventContainerId: Uuid;
                operationConfigurationId: Uuid;
                operationId: Uuid;
                operationItemId: Uuid;
                operationType: Uuid;
                reason: Uuid;
                remarks: Uuid;
                resolvedOrganizationalUnitHierarchy: Uuid;
            };
        };
    };
};

export const eventContainerMovementWorkspaceCapabilityUdoBindingsValidator = zod.strictObject({
    fixedUdoTemplates: zod.strictObject({
        [EventContainerMovementWorkspaceCapabilityUdoTemplateKey.operation]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                actorUserId: uuidZodValidator,
                completedAt: uuidZodValidator,
                eventContainerTemplateId: uuidZodValidator,
                failedCount: uuidZodValidator,
                idempotencyKey: uuidZodValidator,
                ineligibleCount: uuidZodValidator,
                operationConfigurationId: uuidZodValidator,
                operationType: uuidZodValidator,
                reason: uuidZodValidator,
                remarks: uuidZodValidator,
                requestMetadata: uuidZodValidator,
                requestedChanges: uuidZodValidator,
                startedAt: uuidZodValidator,
                movementStatus: uuidZodValidator,
                succeededCount: uuidZodValidator,
                totalCount: uuidZodValidator,
            }),
        }),
        [EventContainerMovementWorkspaceCapabilityUdoTemplateKey.operationItem]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                completedAt: uuidZodValidator,
                eventContainerId: uuidZodValidator,
                expectedRevisionId: uuidZodValidator,
                failureCode: uuidZodValidator,
                failureDetails: uuidZodValidator,
                operationId: uuidZodValidator,
                requestedTargetValues: uuidZodValidator,
                resolvedTargetValues: uuidZodValidator,
                selectionIndex: uuidZodValidator,
                startedAt: uuidZodValidator,
                movementStatus: uuidZodValidator,
                uniqueKey: uuidZodValidator,
            }),
        }),
        [EventContainerMovementWorkspaceCapabilityUdoTemplateKey.audit]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                actorUserId: uuidZodValidator,
                afterValues: uuidZodValidator,
                beforeValues: uuidZodValidator,
                dependentUpdates: uuidZodValidator,
                eventContainerId: uuidZodValidator,
                operationItemId: uuidZodValidator,
                operationConfigurationId: uuidZodValidator,
                operationId: uuidZodValidator,
                operationType: uuidZodValidator,
                reason: uuidZodValidator,
                remarks: uuidZodValidator,
                resolvedOrganizationalUnitHierarchy: uuidZodValidator,
            }),
        }),
    }),
}) satisfies zod.ZodType<EventContainerMovementWorkspaceCapabilityUdoBindings>;
